// Printers, print jobs and the receipt (F-OPS-08 Printer management, F-FNB-05 Kitchen ticket printing, F-STN-13
// Desk mode; spec/13 §11, spec/10 §10, spec/04 §9 `print_job`).
//
//   GET    …/venues/{v}/printers                   the venue's printers, with their status          settings:hardware
//   POST   …/venues/{v}/printers                   add a printer (cloud, desk device, browser)       settings:hardware
//   PATCH  …/venues/{v}/printers/{p}               edit one: name, routing, fallback, drawer         settings:hardware
//   DELETE …/venues/{v}/printers/{p}               archive one                                        settings:hardware
//   POST   …/venues/{v}/printers/{p}/test          a test page through it                            settings:hardware
//   GET    …/venues/{v}/print-jobs                 the recent print jobs, newest first               settings:hardware
//   POST   …/venues/{v}/sales/{s}/receipt          print (or reprint) a sale's receipt                orders:read
//
//   POST   /v1/cloud-print/star/{o}/{v}/{p}/{key}   Star CloudPRNT: the printer's poll                (the key)
//   GET    /v1/cloud-print/star/{o}/{v}/{p}/{key}   Star CloudPRNT: the printer fetches its job      (the key)
//   DELETE /v1/cloud-print/star/{o}/{v}/{p}/{key}   Star CloudPRNT: the printer reports the outcome   (the key)
//   POST   /v1/cloud-print/epson/{o}/{v}/{p}/{key}  Epson Server Direct Print: poll and result       (the key)
//
// **Three transports, one job** (spec/13 §11). A printer is a `cloud` printer that polls the cloud, a `desk` printer
// a desk device (Clavian Counter, spec/10 §10) drives over TCP 9100 or the Windows spooler, or `browser` printing —
// the console opens a print-ready 58/80 mm page. Every job is recorded as a `print_job` with what it printed (the
// print document), where it went and how it ended — `queued` → `sent` → `printed`, or `failed`, or `expired` five
// minutes after it was queued with no answer (spec/09 §6.2) — so a failure is visible rather than silent.
//
// **Routing** (spec/13 §11, F-FNB-05 rule 2): receipts to the printer whose purposes include `receipt` (the counter
// printer); kitchen and bar tickets to the printer for their preparation station. A printer known to be offline hands
// its job to its **fallback printer**; a kitchen ticket with no printer left **falls back to the kitchen display**,
// where it already is, and the desk device gets a `printer_offline` alert (N-18) — nothing is dropped silently. A
// receipt with no printer goes to the browser.
//
// **A cloud printer's URL** carries the organisation, venue and printer it is for, and a key only the printer holds —
// shown once, in the console, when the printer is added (and on a key rotation). The key is stored hashed.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { OrganizationIdSchema, PrinterIdSchema, PrintJobIdSchema, SaleIdSchema, StationCommandIdSchema, StationIdSchema, VenueIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { VenueScopeParamsSchema } from "../primitives/scope.js";
import { DESK_PRINTER_TRANSPORTS, DrawerKickPinSchema, PrintDocumentSchema, PrintPaperWidthSchema } from "../realtime/printing.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { ORDERS_READ, SETTINGS_HARDWARE } from "../permissions/index.js";
// ---------------------------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------------------------
/** How a printer is reached (spec/13 §11): a cloud printer that polls, a desk device's printer, or the browser. */
export const PRINTER_KINDS = ['desk', 'cloud', 'browser'];
export const PrinterKindSchema = z.enum(PRINTER_KINDS).openapi('PrinterKind', {
    description: '`desk`: a desk device drives it (ESC/POS over TCP 9100 or the Windows spooler); `cloud`: it polls the cloud; `browser`: a print-ready page (spec/13 §11).',
});
/** What a printer prints (spec/13 §11 routing): receipts, the kitchen's or the bar's tickets, the day close. */
export const PRINTER_PURPOSES = ['receipt', 'kitchen', 'bar', 'day_close'];
export const PrinterPurposeSchema = z.enum(PRINTER_PURPOSES).openapi('PrinterPurpose', {
    description: 'What a printer is for: `receipt` (the counter printer), `kitchen` and `bar` tickets, `day_close` reports (spec/13 §11).',
});
/** The cloud-print protocols (spec/13 §11): Star CloudPRNT and Epson Server Direct Print. */
export const CLOUD_PRINT_PROTOCOLS = ['star_cloudprnt', 'epson_sdp'];
export const CloudPrintProtocolSchema = z.enum(CLOUD_PRINT_PROTOCOLS).openapi('CloudPrintProtocol');
/**
 * What the platform last knew of a printer: `online` (its last job printed, or a cloud printer polled within the
 * last two minutes), `offline` (a job failed or expired, or a cloud printer stopped polling), `unknown` (nothing yet).
 */
export const PRINTER_STATUSES = ['online', 'offline', 'unknown'];
export const PrinterStatusSchema = z.enum(PRINTER_STATUSES).openapi('PrinterStatus');
/** A cloud printer that has not polled for this long reads as offline, and its jobs fall back (spec/13 §11). */
export const CLOUD_PRINTER_OFFLINE_AFTER_SECONDS = 120;
/**
 * Where a job went (spec/13 §11): a `device` (a desk device's printer), a `cloud` printer, the `browser`, or the
 * kitchen `display` — the fallback when no printer could take a kitchen ticket.
 */
export const PRINT_TARGETS = ['browser', 'cloud', 'device', 'display'];
export const PrintTargetSchema = z.enum(PRINT_TARGETS).openapi('PrintTarget', {
    description: 'Where a print job went: a desk `device`, a `cloud` printer, the `browser`, or the kitchen `display` (spec/13 §11).',
});
/** `queued` → `sent` → `printed`; `failed` when the printer said so; `expired` five minutes after queueing (spec/09 §6.2). */
export const PRINT_JOB_STATUSES = ['queued', 'sent', 'printed', 'failed', 'expired'];
export const PrintJobStatusSchema = z.enum(PRINT_JOB_STATUSES).openapi('PrintJobStatus');
/** What a job printed. */
export const PRINT_CONTENT_TYPES = ['kitchen_ticket', 'receipt', 'test', 'day_close'];
export const PrintContentTypeSchema = z.enum(PRINT_CONTENT_TYPES).openapi('PrintContentType');
/** A print job waits this long for its printer before it expires (spec/09 §6.2 `station.print`). */
export const PRINT_JOB_EXPIRY_SECONDS = 5 * 60;
// ---------------------------------------------------------------------------------------------
// Printers
// ---------------------------------------------------------------------------------------------
/** How a desk device reaches the printer (spec/10 §10). */
export const DeskPrinterConnectionSchema = z
    .object({
    /** The desk device — a station paired with the counter role (Clavian Counter) — that drives the printer. */
    stationId: StationIdSchema,
    transport: z.enum(DESK_PRINTER_TRANSPORTS),
    /** For `tcp`: the printer's IP address or host name on the venue network. */
    host: z.string().trim().min(1).max(255).nullable(),
    /** For `tcp`: the port, 9100 by default. */
    port: z.int().min(1).max(65535).nullable(),
    /** For `spooler`: the Windows printer name exactly as the desk PC lists it (USB printers). */
    queueName: z.string().trim().min(1).max(255).nullable(),
})
    .openapi('DeskPrinterConnection');
/** A cloud printer's protocol and the URL to configure on it (spec/13 §11). */
export const CloudPrinterConnectionSchema = z
    .object({
    protocol: CloudPrintProtocolSchema,
    /**
     * The polling URL to enter on the printer, with its key — returned **only** when the printer is created or its
     * key rotated; null on every other read (the key is stored hashed).
     */
    pollUrl: z.string().url().nullable(),
})
    .openapi('CloudPrinterConnection');
export const PrinterSchema = z
    .object({
    id: PrinterIdSchema,
    venueId: VenueIdSchema,
    name: z.string().min(1).max(80),
    kind: PrinterKindSchema,
    /** What it prints — the routing (spec/13 §11). */
    purposes: z.array(PrinterPurposeSchema),
    paperWidth: PrintPaperWidthSchema,
    /** Set for a `desk` printer; null otherwise. */
    desk: DeskPrinterConnectionSchema.nullable(),
    /** Set for a `cloud` printer; null otherwise. */
    cloud: CloudPrinterConnectionSchema.nullable(),
    /** A cash drawer is wired to this printer and kicks through it (F-OPS-09). Only a `desk` printer can kick one. */
    cashDrawer: z.boolean(),
    /** The drawer's kick pin: 2 (most drawers) or 5. */
    drawerPin: DrawerKickPinSchema,
    /** The printer a job goes to when this one is offline; null for none (spec/13 §11). */
    fallbackPrinterId: PrinterIdSchema.nullable(),
    status: PrinterStatusSchema,
    /** When the printer was last heard from: a printed job, or a cloud printer's poll. */
    lastSeenAt: IsoDateTimeSchema.nullable(),
    /** Why the last job failed, in words staff can act on; null when the last one printed. */
    lastError: z.string().nullable(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .openapi('Printer', { description: 'A printer at a venue and what it prints (spec/13 §11, F-OPS-08).' });
export const PrinterListSchema = z.object({ printers: z.array(PrinterSchema) }).openapi('PrinterList');
const PRINTER_NAME = z.string().trim().min(1).max(80);
/** A desk printer as the console sends it: `host` for `tcp`, `queueName` for `spooler`. */
export const DeskPrinterConnectionInputSchema = z
    .object({
    stationId: StationIdSchema,
    transport: z.enum(DESK_PRINTER_TRANSPORTS),
    host: z.string().trim().min(1).max(255).optional(),
    port: z.int().min(1).max(65535).optional(),
    queueName: z.string().trim().min(1).max(255).optional(),
})
    .refine((value) => (value.transport === 'tcp' ? value.host !== undefined : value.queueName !== undefined), {
    message: 'A tcp printer needs its host; a spooler printer needs its Windows printer name.',
})
    .openapi('DeskPrinterConnectionInput');
export const CreatePrinterSchema = z
    .object({
    name: PRINTER_NAME,
    kind: PrinterKindSchema,
    purposes: z.array(PrinterPurposeSchema).max(PRINTER_PURPOSES.length),
    paperWidth: PrintPaperWidthSchema,
    /** Required for `desk`; refused otherwise. */
    desk: DeskPrinterConnectionInputSchema.optional(),
    /** Required for `cloud` (its protocol); refused otherwise. */
    cloudProtocol: CloudPrintProtocolSchema.optional(),
    cashDrawer: z.boolean().optional(),
    drawerPin: DrawerKickPinSchema.optional(),
    fallbackPrinterId: PrinterIdSchema.nullable().optional(),
})
    .openapi('CreatePrinterRequest');
export const UpdatePrinterSchema = z
    .object({
    name: PRINTER_NAME.optional(),
    purposes: z.array(PrinterPurposeSchema).max(PRINTER_PURPOSES.length).optional(),
    paperWidth: PrintPaperWidthSchema.optional(),
    /** A `desk` printer's connection; refused on any other kind. */
    desk: DeskPrinterConnectionInputSchema.optional(),
    cashDrawer: z.boolean().optional(),
    drawerPin: DrawerKickPinSchema.optional(),
    fallbackPrinterId: PrinterIdSchema.nullable().optional(),
    /** A `cloud` printer: issue a new key; the answer carries the new polling URL once, and the old one stops working. */
    rotateCloudKey: z.boolean().optional(),
})
    .openapi('UpdatePrinterRequest');
// ---------------------------------------------------------------------------------------------
// Print jobs
// ---------------------------------------------------------------------------------------------
export const PrintJobSchema = z
    .object({
    id: PrintJobIdSchema,
    venueId: VenueIdSchema,
    /** The printer it was routed to; null for the browser with no printer, or the kitchen display. */
    printerId: PrinterIdSchema.nullable(),
    /** The purpose it was routed by (a `PrinterPurpose`); null for a test page. */
    purpose: z.string().nullable(),
    contentType: PrintContentTypeSchema,
    target: PrintTargetSchema,
    status: PrintJobStatusSchema,
    /** What it prints, laid out by the transport (spec/13 §11). */
    document: PrintDocumentSchema,
    /** The desk device it was sent to (spec/04 §9 `deviceId?`), for a `device` job. */
    deviceStationId: StationIdSchema.nullable(),
    /** The `station.print` command that carries it, for a `device` job. */
    commandId: StationCommandIdSchema.nullable(),
    /** The job this one reprints (a reprint is a new job, F-FNB-05 rule 6); null for a first print. */
    reprintOf: PrintJobIdSchema.nullable(),
    /** Why it failed, fell back or expired; null otherwise. */
    error: z.string().nullable(),
    createdAt: IsoDateTimeSchema,
    sentAt: IsoDateTimeSchema.nullable(),
    completedAt: IsoDateTimeSchema.nullable(),
    expiresAt: IsoDateTimeSchema,
})
    .openapi('PrintJob', { description: 'One print job and how it ended (spec/04 §9 `print_job`, F-OPS-08).' });
export const PrintJobListSchema = z.object({ jobs: z.array(PrintJobSchema) }).openapi('PrintJobList');
// ---------------------------------------------------------------------------------------------
// Routes
// ---------------------------------------------------------------------------------------------
const PrinterParamsSchema = VenueScopeParamsSchema.extend({ printerId: PrinterIdSchema }).openapi('PrinterParams');
const SaleReceiptParamsSchema = VenueScopeParamsSchema.extend({ saleId: SaleIdSchema }).openapi('SaleReceiptParams');
const PrintJobQuerySchema = z
    .object({ limit: z.coerce.number().int().min(1).max(100).optional().openapi({ description: 'At most this many; 50 by default.' }) })
    .openapi('PrintJobQuery');
const venueNotFound = problemResponse('No such venue in this organisation.', 'not-found');
const printerNotFound = problemResponse('No such printer at this venue.', 'not-found');
export const listPrintersRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/printers',
    operationId: 'listPrinters',
    tags: ['printers'],
    summary: 'A venue’s printers (F-OPS-08)',
    description: 'The venue’s printers with their routing and status, by name. Requires `settings:hardware`.',
    [PERMISSION_EXTENSION]: SETTINGS_HARDWARE,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: jsonResponse(PrinterListSchema, 'The venue’s printers.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: venueNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const createPrinterRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/printers',
    operationId: 'createPrinter',
    tags: ['printers'],
    summary: 'Add a printer (F-OPS-08)',
    description: 'A cloud printer (the answer carries its polling URL, once), a desk device printer (TCP 9100 or a Windows spooler ' +
        'queue on a Clavian Counter PC), or browser printing. Requires `settings:hardware`. Audited.',
    [PERMISSION_EXTENSION]: SETTINGS_HARDWARE,
    request: {
        params: VenueScopeParamsSchema,
        body: { content: { 'application/json': { schema: CreatePrinterSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(PrinterSchema, 'The printer that was added.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: venueNotFound,
        409: problemResponse('The desk device is not a Clavian Counter PC of this venue, or the fallback is not a printer of it.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const updatePrinterRoute = createRoute({
    method: 'patch',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/printers/{printerId}',
    operationId: 'updatePrinter',
    tags: ['printers'],
    summary: 'Edit a printer (F-OPS-08)',
    description: 'Only the fields sent change. Requires `settings:hardware`. Audited with the before and after.',
    [PERMISSION_EXTENSION]: SETTINGS_HARDWARE,
    request: {
        params: PrinterParamsSchema,
        body: { content: { 'application/json': { schema: UpdatePrinterSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(PrinterSchema, 'The printer as it is now.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: printerNotFound,
        409: problemResponse('The desk device or the fallback is not valid for this printer.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const archivePrinterRoute = createRoute({
    method: 'delete',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/printers/{printerId}',
    operationId: 'archivePrinter',
    tags: ['printers'],
    summary: 'Archive a printer (F-OPS-08)',
    description: 'It stops receiving jobs; its past jobs keep naming it. A printer that is another’s fallback stops being one. ' +
        'Requires `settings:hardware`. Audited.',
    [PERMISSION_EXTENSION]: SETTINGS_HARDWARE,
    request: { params: PrinterParamsSchema },
    responses: {
        204: { description: 'Archived.' },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: printerNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const testPrinterRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/printers/{printerId}/test',
    operationId: 'testPrinter',
    tags: ['printers'],
    summary: 'Print a test page (F-OPS-08)',
    description: 'A test page — the printer’s name, width, transport and the time — straight to this printer, never to its ' +
        'fallback. A desk printer’s job goes to its desk device as `station.print`; a cloud printer collects it on its ' +
        'next poll; a browser printer answers with the page to print. Requires `settings:hardware`.',
    [PERMISSION_EXTENSION]: SETTINGS_HARDWARE,
    request: { params: PrinterParamsSchema },
    responses: {
        202: jsonResponse(PrintJobSchema, 'The test job, on its way.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: printerNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const listPrintJobsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/print-jobs',
    operationId: 'listPrintJobs',
    tags: ['printers'],
    summary: 'The venue’s recent print jobs (F-OPS-08)',
    description: 'Newest first, with where each went and how it ended — so a failure is visible. Requires `settings:hardware`.',
    [PERMISSION_EXTENSION]: SETTINGS_HARDWARE,
    request: { params: VenueScopeParamsSchema, query: PrintJobQuerySchema },
    responses: {
        200: jsonResponse(PrintJobListSchema, 'The recent print jobs.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: venueNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
/** What printing a receipt answers: the job, and where it went — the browser prints `job.document` itself. */
export const ReceiptPrintSchema = z
    .object({
    job: PrintJobSchema,
    target: PrintTargetSchema,
})
    .openapi('ReceiptPrint');
export const printSaleReceiptRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/sales/{saleId}/receipt',
    operationId: 'printSaleReceipt',
    tags: ['printers'],
    summary: 'Print or reprint a sale’s receipt (F-OPS-08)',
    description: 'Renders the sale’s receipt for an 80 or 58 mm roll — its document’s title and number, the venue, the lines, the ' +
        'tax, the tenders and the change — and routes it to the receipt printer, or its fallback, or the browser when ' +
        'there is none. A reprint is a new job. Requires `orders:read`.',
    [PERMISSION_EXTENSION]: ORDERS_READ,
    request: { params: SaleReceiptParamsSchema },
    responses: {
        202: jsonResponse(ReceiptPrintSchema, 'The receipt job and where it went.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such sale at this venue.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Cloud printers' polling (unauthenticated by session; the key in the URL is the credential)
// ---------------------------------------------------------------------------------------------
/** The key a cloud printer's URL carries: 43 base64url characters (32 random bytes). */
export const CLOUD_PRINT_KEY_PATTERN = /^[A-Za-z0-9_-]{43}$/;
const CloudPrintParamsSchema = z
    .object({
    organizationId: OrganizationIdSchema,
    venueId: VenueIdSchema,
    printerId: PrinterIdSchema,
    key: z.string().regex(CLOUD_PRINT_KEY_PATTERN),
})
    .openapi('CloudPrintParams');
/** What Star CloudPRNT sends when it polls: its status. Only the fields the platform reads are named. */
export const StarCloudPrntPollSchema = z
    .looseObject({
    status: z.string().optional(),
    printerMAC: z.string().optional(),
    statusCode: z.string().optional(),
    printingInProgress: z.boolean().optional(),
})
    .openapi('StarCloudPrntPoll');
/** What the platform answers a CloudPRNT poll (Star's protocol): whether a job waits and the media it comes in. */
export const StarCloudPrntPollAnswerSchema = z
    .object({
    jobReady: z.boolean(),
    mediaTypes: z.array(z.string()).optional(),
    jobToken: z.string().optional(),
})
    .openapi('StarCloudPrntPollAnswer');
export const starCloudPrntPollRoute = createRoute({
    method: 'post',
    path: '/v1/cloud-print/star/{organizationId}/{venueId}/{printerId}/{key}',
    operationId: 'starCloudPrntPoll',
    tags: ['printers'],
    summary: 'Star CloudPRNT poll (F-OPS-08)',
    description: 'The printer reports its status and learns whether a job waits. Marks the printer online. The URL’s key is the credential.',
    request: {
        params: CloudPrintParamsSchema,
        body: { content: { 'application/json': { schema: StarCloudPrntPollSchema } }, required: false },
    },
    responses: {
        200: jsonResponse(StarCloudPrntPollAnswerSchema, 'Whether a job waits.'),
        400: commonProblemResponses[400],
        404: problemResponse('No such cloud printer, or the key does not match.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const starCloudPrntJobRoute = createRoute({
    method: 'get',
    path: '/v1/cloud-print/star/{organizationId}/{venueId}/{printerId}/{key}',
    operationId: 'starCloudPrntJob',
    tags: ['printers'],
    summary: 'Star CloudPRNT job fetch (F-OPS-08)',
    description: 'The oldest waiting job, as plain text laid out for the roll. The job becomes `sent`.',
    request: {
        params: CloudPrintParamsSchema,
        query: z.object({ token: z.string().optional(), type: z.string().optional(), mac: z.string().optional() }).openapi('StarCloudPrntJobQuery'),
    },
    responses: {
        200: { description: 'The job.', content: { 'text/plain': { schema: z.string() } } },
        404: problemResponse('No such cloud printer, no key match, or no job waiting.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const starCloudPrntConfirmRoute = createRoute({
    method: 'delete',
    path: '/v1/cloud-print/star/{organizationId}/{venueId}/{printerId}/{key}',
    operationId: 'starCloudPrntConfirm',
    tags: ['printers'],
    summary: 'Star CloudPRNT job outcome (F-OPS-08)',
    description: 'The printer reports how the job it fetched went: a `code` starting with 2 is printed, anything else failed.',
    request: {
        params: CloudPrintParamsSchema,
        query: z.object({ token: z.string().optional(), code: z.string().optional(), mac: z.string().optional() }).openapi('StarCloudPrntConfirmQuery'),
    },
    responses: {
        200: { description: 'Recorded.' },
        404: problemResponse('No such cloud printer, or the key does not match.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const epsonServerDirectPrintRoute = createRoute({
    method: 'post',
    path: '/v1/cloud-print/epson/{organizationId}/{venueId}/{printerId}/{key}',
    operationId: 'epsonServerDirectPrint',
    tags: ['printers'],
    summary: 'Epson Server Direct Print poll and result (F-OPS-08)',
    description: 'A form post: `ConnectionType=GetRequest` asks for a job and is answered with ePOS-Print XML (or an empty ' +
        'answer); `ConnectionType=SetResponse` reports how the last job went.',
    request: { params: CloudPrintParamsSchema },
    responses: {
        200: { description: 'The job as ePOS-Print XML, or empty.', content: { 'text/xml': { schema: z.string() } } },
        404: problemResponse('No such cloud printer, or the key does not match.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=printers.js.map